/**
 * Trustpilot lazy-load bootstrap. No `import` statements: `window.Trustpilot` is provided by Trustpilot’s script
 * (theme app / external bootstrap), not an ES module path in this repo.
 *
 * Loaded from `layout/theme.liquid` on collection + search only. Elsewhere the snippet uses
 * `trustpilot-widget` and Trustpilot’s bootstrap handles init. Markup: `snippets/trustpilot-star-rating-widget.liquid`.
 */

const INIT_KEY = '__acaiTrustpilotLazyInit';
if (!window[INIT_KEY]) {
  window[INIT_KEY] = true;

  /**
   * @typedef {Object} LazyPreloadConfig
   * @property {number} rowHeightPx
   * @property {number} rowsAhead
   */

  /** Two product-grid rows at 600px row height → 1200px preload below the fold. */
  /** @type {LazyPreloadConfig} */
  const LAZY_PRELOAD = {
    rowHeightPx: 600,
    rowsAhead: 2,
  };

  const preloadBelowPx = LAZY_PRELOAD.rowHeightPx * LAZY_PRELOAD.rowsAhead;

  const getViewportHeight = () =>
    window.innerHeight || document.documentElement?.clientHeight || 0;

  /**
   * @typedef {Object} TryLoadOptions
   * @property {HTMLElement} target
   * @property {IntersectionObserver} observer
   */

  /**
   * Activates Trustpilot on the target element when `window.Trustpilot` is available.
   * @param {TryLoadOptions} options
   * @returns {boolean} True if loaded or already done; false if Trustpilot is not ready.
   */
  const tryLoadTrustpilotWidget = ({ target, observer }) => {
    if (!target?.classList?.contains('trustpilot-widget-lazy')) {
      return true;
    }
    if (typeof window.Trustpilot === 'undefined' || window.Trustpilot === null) {
      return false;
    }
    target.classList.remove('trustpilot-widget-lazy', 'js-trustpilot-widget-lazy');
    target.classList.add('trustpilot-widget');
    window.Trustpilot.loadFromElement(target);
    observer.unobserve(target);
    return true;
  };

  /**
   * True when the element overlaps the viewport, is fully above it, or sits in the preload band below the fold.
   * @param {Object} options
   * @param {HTMLElement} options.element
   * @param {number} options.viewportHeight
   * @param {number} options.preloadBelowPx
   * @returns {boolean}
   */
  const isVisibleOrScrolledPast = ({ element, viewportHeight: vh, preloadBelowPx: band }) => {
    if (!element) {
      return false;
    }
    const rect = element.getBoundingClientRect();
    const overlapsViewportVertically = rect.top < vh && rect.bottom > 0;
    const entirelyAboveViewport = rect.bottom < 0;
    const inPreloadBelowFold = rect.top >= vh && rect.top <= vh + band;
    return overlapsViewportVertically || entirelyAboveViewport || inPreloadBelowFold;
  };

  /**
   * @typedef {Object} InitObserverOptions
   * @property {number} preloadBelowPx
   */

  /**
   * @param {InitObserverOptions} options
   * @returns {IntersectionObserver}
   */
  const createTrustpilotObserver = ({ preloadBelowPx: marginPx }) =>
    new IntersectionObserver(
      (entries, obs) => {
        entries
          .filter((entry) => entry.isIntersecting === true)
          .forEach((entry) => {
            const target = entry.target;
            if (!(target instanceof HTMLElement)) {
              return;
            }
            tryLoadTrustpilotWidget({ target, observer: obs });
          });
      },
      {
        rootMargin: `0px 0px ${marginPx}px 0px`,
        threshold: 0.001,
      }
    );

  const observer = createTrustpilotObserver({ preloadBelowPx });

  /**
   * Re-scan all lazy widgets (fresh layout + viewport). Call after paint, window load, and Trustpilot bootstrap.
   * @param {IntersectionObserver} obs
   */
  const reconcileLazyTrustpilotWidgets = (obs) => {
    const viewportHeight = getViewportHeight();
    Array.from(document.querySelectorAll('.js-trustpilot-widget-lazy')).forEach((widget) => {
      if (!(widget instanceof HTMLElement)) {
        return;
      }
      if (
        isVisibleOrScrolledPast({
          element: widget,
          viewportHeight,
          preloadBelowPx,
        })
      ) {
        if (!tryLoadTrustpilotWidget({ target: widget, observer: obs })) {
          obs.observe(widget);
        }
      } else {
        obs.observe(widget);
      }
    });
  };

  reconcileLazyTrustpilotWidgets(observer);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      reconcileLazyTrustpilotWidgets(observer);
    });
  });

  window.addEventListener('load', () => {
    reconcileLazyTrustpilotWidgets(observer);
  });

  window.addEventListener('trustpilot-bootstrap', () => {
    reconcileLazyTrustpilotWidgets(observer);
  });

  /** After facet / section HTML swap (`theme.js`), new product cards need observing. */
  document.documentElement.addEventListener(
    'theme:loading:end',
    () => {
      requestAnimationFrame(() => {
        reconcileLazyTrustpilotWidgets(observer);
      });
    },
    { passive: true }
  );

  document.addEventListener(
    'shopify:section:load',
    () => {
      requestAnimationFrame(() => {
        reconcileLazyTrustpilotWidgets(observer);
      });
    },
    { passive: true }
  );
}
